import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Star, ShoppingBag, Truck, Shield } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const { add } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get(`/products/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-16 text-[#718096]">Loading…</div>;
  const { product, reviews, vendor } = data;

  const submitReview = async () => {
    if (!user) { toast.error("Please sign in to review"); navigate("/auth"); return; }
    if (!comment.trim()) return;
    try {
      await api.post("/reviews", { product_id: product.id, rating, comment });
      toast.success("Review posted");
      setComment("");
      load();
    } catch { toast.error("Failed to post review"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-2xl overflow-hidden bg-[#F2EFEB]">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
          ) : <div className="w-full h-full grid place-items-center font-serif text-6xl text-[#C3B9A6]">स</div>}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="badge-swadeshi">Swadeshi</span>
            <span className="label-caps">{product.region} · {product.category}</span>
          </div>
          <h1 className="text-4xl md:text-5xl leading-tight" data-testid="product-title">{product.title}</h1>
          {vendor && (
            <Link to={`/vendor/${vendor.id}`} className="mt-3 inline-block text-sm text-[#E25A24] hover:underline">
              by {vendor.business_name} · {vendor.location}
            </Link>
          )}
          <div className="mt-4 flex items-center gap-3">
            <div className="text-3xl font-bold text-[#0D141C]">₹{product.price.toLocaleString('en-IN')}</div>
            {product.rating > 0 && (
              <div className="flex items-center gap-1 text-sm text-[#4A5568]">
                <Star className="w-4 h-4 fill-[#E25A24] text-[#E25A24]" />
                {product.rating} ({product.review_count})
              </div>
            )}
          </div>
          <p className="mt-6 text-[#4A5568] leading-relaxed">{product.description}</p>
          <div className="mt-8 flex gap-3">
            <button
              data-testid="add-to-cart-btn"
              onClick={() => { add(product); toast.success("Added to cart"); }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" /> Add to cart
            </button>
            <button
              data-testid="buy-now-btn"
              onClick={() => { add(product); navigate("/cart"); }}
              className="btn-outline"
            >
              Buy now
            </button>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="p-4 bg-[#F2EFEB] rounded-xl flex items-start gap-3">
              <Truck className="w-5 h-5 text-[#275F36] mt-0.5" />
              <div>
                <div className="font-semibold text-sm text-[#0D141C]">Ships from {product.region}</div>
                <div className="text-xs text-[#718096]">5-7 business days</div>
              </div>
            </div>
            <div className="p-4 bg-[#F2EFEB] rounded-xl flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#275F36] mt-0.5" />
              <div>
                <div className="font-semibold text-sm text-[#0D141C]">7-day returns</div>
                <div className="text-xs text-[#718096]">Easy hassle-free</div>
              </div>
            </div>
          </div>

          {vendor?.story && (
            <div className="mt-10 p-6 border border-[#E2DCD0] rounded-2xl bg-white">
              <div className="label-caps mb-2">The maker&apos;s story</div>
              <p className="text-sm text-[#4A5568] leading-relaxed">{vendor.story}</p>
            </div>
          )}
        </div>
      </div>

      {/* REVIEWS */}
      <div className="mt-20">
        <h2 className="text-3xl mb-6">Reviews</h2>
        {user && (
          <div className="card-flat p-6 mb-8">
            <div className="label-caps mb-3">Write a review</div>
            <div className="flex gap-1 mb-3">
              {[1,2,3,4,5].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-[#E25A24] text-[#E25A24]" : "text-[#C3B9A6]"}`} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-comment-input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience…"
              className="w-full px-3 py-2 border border-[#E2DCD0] rounded-lg text-sm focus:outline-none focus:border-[#E25A24]"
              rows={3}
            />
            <button onClick={submitReview} data-testid="review-submit" className="mt-3 btn-primary !py-2 !px-5 text-sm">Post review</button>
          </div>
        )}
        {reviews.length === 0 ? (
          <div className="text-sm text-[#718096]">Be the first to review this product.</div>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="card-flat p-5">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#0D141C]">{r.user_name}</div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#E25A24] text-[#E25A24]" : "text-[#C3B9A6]"}`} />
                    ))}
                  </div>
                </div>
                <p className="mt-2 text-sm text-[#4A5568]">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
